import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import { toast } from "react-toastify";
import { yupResolver } from "@hookform/resolvers/yup";
import { FieldError, useForm } from "react-hook-form";
import { ApolloQueryResult } from "@apollo/client";

import { PanelWidth } from "../../../../components/panel/Panel";
import { DagCommunityViewQuery, Exact, useConvertDagsToScrMutation, ViewerQuery } from "../../../../graphql/schema";
import { Button } from "../../../../components/button/Button";
import { Color } from "../../../../services/buttonLinkConstants";
import { Layout } from "../../../../components/layout/Layout";
import { DataFraction } from "../../../../components/data-fraction/DataFraction";
import DateTime from "../../../../components/date-time/DateTime";
import { Modal, ModalKind } from "../../../../components/modal/Modal";
import { showGraphqlValidationErrors } from "../../../../services/showGraphqlValidationErrors";
import Field from "../../../../components/field/Field";

import styles from "./dag-community-view.module.scss";

export interface DagCommunityDagsToScrProps {
  me: ViewerQuery | undefined;
  totalDagsQuery: DagCommunityViewQuery | undefined;
  meRefetch: (
    variables?:
      | Partial<
          Exact<{
            [key: string]: never;
          }>
        >
      | undefined,
  ) => Promise<ApolloQueryResult<ViewerQuery>>;
  className?: string;
}

interface BurnDagsFormInfo {
  totalAmount: string;
  confirm: boolean;
  final: boolean;
}

const burnDagsValidationSchema: yup.SchemaOf<BurnDagsFormInfo> = yup.object().shape({
  totalAmount: yup.string().required("Amount is required"),
  confirm: yup
    .boolean()
    .oneOf([true], "Please check that you understand the terms")
    .required("Please check that you understand the terms"),
  final: yup
    .boolean()
    .oneOf([true], "Please check that you understand that burn is final and cannot be reversed.")
    .required("Please check that you understand that burn is final and cannot be reversed."),
});

function useBurnDagsForm() {
  const { register, handleSubmit, errors, reset, setError } = useForm<BurnDagsFormInfo>({
    mode: "all",
    reValidateMode: "onChange",
    resolver: yupResolver(burnDagsValidationSchema),
  });

  return {
    register,
    handleSubmit,
    errors,
    reset,
    setError,
  };
}

export const DagCommunityDagsToScr: FC<DagCommunityDagsToScrProps> = ({ me, meRefetch }) => {
  const { t } = useTranslation();
  const [amount, setAmount] = useState("");
  const [isBurnMode, setIsBurnMode] = useState(false);
  const [convertDags] = useConvertDagsToScrMutation();

  const {
    register: burnDags,
    handleSubmit: handleBurnDagsSubmit,
    errors: burnDagsInputErrors,
    reset: resetBurnDags,
    setError: setBurnDagsError,
  } = useBurnDagsForm();

  function getDagsAmount() {
    return parseFloat(me?.me.dagsInfo?.totalDags ?? "0") - parseFloat(me?.me.dagsInfo?.usedDags ?? "0");
  }

  function getScrAmount() {
    return (getDagsAmount() / parseFloat(me?.me.dagsInfo?.ratio ?? "0")).toFixed(2);
  }
  const totalDags = getDagsAmount();

  const handlePercentage = (percent: string) => {
    setAmount(((totalDags * parseFloat(percent)) / 100).toString());
  };

  const handleBurnDagsFormSubmit = handleBurnDagsSubmit(async ({ totalAmount }) => {
    try {
      const result = await convertDags({
        variables: { totalAmount },
      });
      if (result.data) {
        toast.success(t("DAGS burned to SCR successfully!"));
        resetBurnDags();
        setAmount("");
        meRefetch();
        // setIsWebinarsModalOpen(false);
      } else {
        const errorMessage = result?.errors?.[0]?.message;
        if (errorMessage) {
          const myCustomFieldError: FieldError = {
            type: "required",
            message: errorMessage,
            ref: undefined,
          };

          burnDagsInputErrors.totalAmount = myCustomFieldError;

          setBurnDagsError("totalAmount", {
            type: "manual",
            message: errorMessage || t("An unexpected error occurred"),
          });
        } else {
          toast.error(t("Something went wrong"));
        }
      }
    } catch (err: any) {
      setBurnDagsError("totalAmount", {
        type: "manual",
        message: err?.message || t("An unexpected error occurred"),
      });
      if (!showGraphqlValidationErrors(t, err) && err instanceof Error) {
        toast.error(err?.message);
      }
    }
  });

  return (
    <>
      <h2 style={{ margin: "15px 0" }}>Early Adopters Burn Incentive</h2>
      <ul>
        <li className={styles["dag-wrap"]}>
          <div className={styles["dag-section"]}>
            <div>
              <h3 className={styles.name}>Burn DAGS and receive Shop Credits (SCR)</h3>
            </div>
          </div>

          <Layout className={styles["dag-data"]}>
            <DataFraction
              className={styles["data-fraction"]}
              icon={undefined}
              label={t("Your DAGS balance")}
              value={`${me?.viewer?.coinAccountBalance} ${t("DAGS")}`}
            />
            <DataFraction
              className={styles["data-fraction"]}
              icon={undefined}
              label={t("DAGS You can burn")}
              value={`${getDagsAmount()} ${t("DAGS")}`}
            />

            <DataFraction
              className={styles["data-fraction"]}
              icon={undefined}
              label={t("You receive")}
              value={`${getScrAmount()} ${t("SCR")}`}
            />

            <Button
              className={styles["access-product-link"]}
              borderRadius="MEDIUM"
              color={Color.DARK_BLUE}
              fontSize={14}
              onClick={() => {
                setIsBurnMode(true);
              }}
            >
              <span className={styles["link-label"]}>{t("Go to Burn")}</span>
            </Button>
          </Layout>
        </li>
      </ul>

      {me?.me?.dagsInfo?.burned &&
        me?.me?.dagsInfo?.burned?.map((burned, index) => (
          <div key={`${index}-term`} className={styles["term-grid-normal"]}>
            <div>
              <strong>Burned DAGS:</strong> {burned.dags}
            </div>
            <div>
              <strong>SCR: </strong>
              {parseFloat(burned.scr).toFixed(2)}
            </div>
            <div>
              <DateTime value={burned.createdAt} />
            </div>
          </div>
        ))}

      <Modal
        isOpen={isBurnMode}
        close={() => {
          setIsBurnMode(false); // Reset modal content on close
        }}
        kind={ModalKind.PRIMARY}
        panelProps={{
          imagePath: "/images/crowd-icon.svg",
          width: PanelWidth.SMALL,
          title: t("Burn DAGS and receive SCR"),
          caption: t("Burn DAGS and receive SCR"),
          label: t("Burn DAGS and receive SCR"),
          titleClassName: styles["modal-title"],
          captionClassName: styles["modal-caption"],
        }}
      >
        <div>
          {/* Burn DAGS Content */}
          <h2 style={{ textAlign: "center", marginTop: "30px" }}>{t("Burn DAGS and receive SCR")}</h2>
          <form onSubmit={handleBurnDagsFormSubmit}>
            <div style={{ margin: "20px 0" }}>
              <Field
                type="number"
                className={styles.field}
                label={t("Total DAGS to Burn")}
                name="totalAmount"
                gutter={20}
                error={burnDagsInputErrors.totalAmount}
                internalRef={burnDags}
                value={amount}
                onChange={(e) => {
                  setAmount(e);
                }}
              />
            </div>
            <p>
              <strong>You receive SCR:</strong>{" "}
              {parseFloat(amount ?? "0") > 0
                ? (parseFloat(amount ?? "0") / parseFloat(me?.me?.dagsInfo?.ratio ?? "0")).toFixed(2)
                : "0"}
            </p>
            <div style={{ display: "flex", justifyContent: "space-between", margin: "15px 0" }}>
              {["0", "25", "50", "75", "100"].map((percent) => (
                <button
                  type={"button"}
                  key={percent}
                  onClick={() => handlePercentage(percent)}
                  style={{
                    flex: 1,
                    margin: "0 5px",
                    padding: "10px",
                    backgroundColor: "#007bff",
                    color: "#fff",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}
                >
                  {percent}%
                </button>
              ))}
            </div>
            <div style={{ margin: "20px 0" }}>
              <input
                type="range"
                min="0"
                max={totalDags}
                value={amount}
                onChange={(e) => {
                  if (parseFloat(e.target.value) > totalDags) {
                    setAmount(totalDags.toString());
                  } else {
                    setAmount(parseFloat(e.target.value).toString());
                  }
                }}
                style={{ width: "100%" }}
              />
            </div>
            <Field
              type="checkbox"
              className={styles.field}
              label={t("I acknowledge that the burn is final and can not be reversed.")}
              name="final"
              gutter={20}
              error={burnDagsInputErrors.final}
              internalRef={burnDags}
            />
            <Field
              type="checkbox"
              className={styles.field}
              label={t("I confirm that I have reviewed all details, and I understand the terms of the DAGS burn.")}
              name="confirm"
              gutter={20}
              error={burnDagsInputErrors.confirm}
              internalRef={burnDags}
            />
            <div style={{ textAlign: "center", width: "100%", alignItems: "center" }}>
              <Button
                className={styles["access-product-link"]}
                borderRadius="MEDIUM"
                color={Color.DARK_BLUE}
                fontSize={14}
              >
                <span className={styles["link-label"]}>{t("Confirm Burn")}</span>
              </Button>
            </div>
          </form>
        </div>
      </Modal>
    </>
  );
};
